import { INPUT } from '../config/tuning';
import { clamp } from '../flight/Aerodynamics';
import { PadAxis, PadButton, type Bindings, type InputActionId } from './Bindings';
import { deadzone, type AxisSample, type LookCommand } from './KeyboardMouse';

/**
 * Gamepad input (SPEC §3.13).
 *
 * Per-axis deadzone, sensitivity, and an expo curve. Expo is not a nicety: it is what lets one
 * stick serve both a gentle course correction and a last-ditch break, because it trades
 * resolution near centre for reach at the edges.
 *
 * The `Gamepad` API is polled, not evented, so this samples `navigator.getGamepads()` once per
 * tick. The navigator access is the external boundary; everything below `readPad` is pure.
 */

/** A snapshot of the axes and buttons, so the pure logic can be tested without a browser. */
export interface PadSnapshot {
  connected: boolean;
  axes: readonly number[];
  buttons: readonly number[];
}

export const EMPTY_PAD: PadSnapshot = { connected: false, axes: [], buttons: [] };

/**
 * Deadzone then expo. `expo` blends between linear (0) and cubic (1); the default 0.35 keeps
 * roughly a third of the curve's shape from the cube term.
 */
export function applyDeadzoneExpo(raw: number, deadzoneThreshold: number, expo: number): number {
  const shaped = deadzone(clamp(raw, -1, 1), deadzoneThreshold);
  return (1 - expo) * shaped + expo * shaped * shaped * shaped;
}

/**
 * Map a pad snapshot to flight axes. Pure — this is the part worth testing.
 *
 * In look mode the right stick is borrowed for the camera, so its yaw and throttle contributions
 * are suppressed: a player glancing over their shoulder must not also be firewalling the throttle.
 */
export function padToAxes(
  pad: PadSnapshot,
  bindings: Bindings,
  out: AxisSample,
  lookMode = false,
): AxisSample {
  out.roll = 0;
  out.pitch = 0;
  out.yaw = 0;
  out.throttleRate = 0;
  if (!pad.connected) return out;

  const options = bindings.options;
  const dead = options.gamepadDeadzone;
  const expo = options.gamepadExpo;

  out.roll = applyDeadzoneExpo(axis(pad, PadAxis.LeftX), dead, expo);

  // Stick forward (negative Y) lowers the nose, matching the keyboard sense.
  const pitchSign = options.invertPitch ? 1 : -1;
  out.pitch = applyDeadzoneExpo(axis(pad, PadAxis.LeftY), dead, expo) * pitchSign;

  // Yaw from the triggers, falling back to the right stick when they are idle.
  const triggerYaw =
    triggerValue(pad, PadButton.RT) - triggerValue(pad, PadButton.LT);
  const rightStickYaw = lookMode ? 0 : axis(pad, PadAxis.RightX);
  const yaw =
    Math.abs(triggerYaw) > INPUT.padTriggerDeadzone ? triggerYaw : rightStickYaw;
  out.yaw = applyDeadzoneExpo(yaw, dead, expo);

  // Right stick Y drives throttle as a rate, not an absolute position: a self-centring stick
  // cannot hold an absolute throttle setting.
  const throttleAxis = lookMode ? 0 : applyDeadzoneExpo(-axis(pad, PadAxis.RightY), dead, expo);
  out.throttleRate = throttleAxis * INPUT.throttleRatePerS;

  return out;
}

/** Map the right stick to look-around rates while look mode is held (SPEC §3.13). */
export function padToLook(pad: PadSnapshot, bindings: Bindings, out: LookCommand): LookCommand {
  out.rateX = 0;
  out.rateY = 0;
  out.deltaXRad = 0;
  out.deltaYRad = 0;
  out.active = false;
  if (!pad.connected) return out;

  const options = bindings.options;
  out.rateX = applyDeadzoneExpo(axis(pad, PadAxis.RightX), options.gamepadDeadzone, options.gamepadExpo);
  out.rateY = applyDeadzoneExpo(-axis(pad, PadAxis.RightY), options.gamepadDeadzone, options.gamepadExpo);
  out.active = out.rateX !== 0 || out.rateY !== 0;
  return out;
}

/** True if any pad button bound to the action is pressed in the snapshot. */
export function isPadActionPressed(
  pad: PadSnapshot,
  bindings: Bindings,
  action: InputActionId,
): boolean {
  const buttons = bindings.gamepadButtons[action];
  if (buttons === undefined) return false;
  for (let i = 0; i < buttons.length; i += 1) {
    if ((pad.buttons[buttons[i]] ?? 0) > 0.5) return true;
  }
  return false;
}

function axis(pad: PadSnapshot, index: number): number {
  return pad.axes[index] ?? 0;
}

function triggerValue(pad: PadSnapshot, index: number): number {
  return pad.buttons[index] ?? 0;
}

export class GamepadInput {
  readonly bindings: Bindings;

  /** Latest snapshot; replaced each poll. Pre-allocated arrays, so polling does not allocate. */
  private readonly snapshot = {
    connected: false,
    axes: new Array<number>(8).fill(0),
    buttons: new Array<number>(20).fill(0),
  };

  private readonly previousButtons = new Array<number>(20).fill(0);
  private readonly pendingActions = new Set<InputActionId>();

  constructor(bindings: Bindings) {
    this.bindings = bindings;
  }

  get connected(): boolean {
    return this.snapshot.connected;
  }

  get pad(): PadSnapshot {
    return this.snapshot;
  }

  /**
   * Read the first connected pad into the snapshot and record button edges.
   * `navigator.getGamepads` is an external boundary: absent in Node, and it returns a sparse
   * array with null holes in every browser.
   */
  poll(): void {
    this.snapshot.connected = false;
    if (!this.bindings.options.gamepadEnabled) return;
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return;

    const pads = navigator.getGamepads();
    let active: Gamepad | null = null;
    for (let i = 0; i < pads.length; i += 1) {
      const candidate = pads[i];
      if (candidate !== null && candidate.connected) {
        active = candidate;
        break;
      }
    }
    if (active === null) return;

    this.snapshot.connected = true;
    const axes = active.axes;
    for (let i = 0; i < this.snapshot.axes.length; i += 1) {
      this.snapshot.axes[i] = i < axes.length ? axes[i] : 0;
    }
    const buttons = active.buttons;
    for (let i = 0; i < this.snapshot.buttons.length; i += 1) {
      const value = i < buttons.length ? buttons[i].value : 0;
      if (value > 0.5 && this.previousButtons[i] <= 0.5) this.recordEdges(i);
      this.previousButtons[i] = value;
      this.snapshot.buttons[i] = value;
    }
  }

  /** Write the pad's contribution to the flight axes. */
  sample(out: AxisSample, lookMode = false): AxisSample {
    return padToAxes(this.snapshot, this.bindings, out, lookMode);
  }

  consumeAction(action: InputActionId): boolean {
    return this.pendingActions.delete(action);
  }

  clearPendingActions(): void {
    this.pendingActions.clear();
  }

  private recordEdges(buttonIndex: number): void {
    const bound = this.bindings.gamepadButtons;
    for (const action of Object.keys(bound) as InputActionId[]) {
      const indices = bound[action];
      if (indices !== undefined && indices.includes(buttonIndex)) {
        this.pendingActions.add(action);
      }
    }
  }
}
